import type { EmbeddingErrorCode } from './protocol'

const NON_COMPATIBILITY_FAILURE =
  /\b(?:abort|allowlist|blocked|cache|checksum|cors|fetch|http|integrity|manifest|network|offline|quota|request|response|status\s+\d{3}|timed?\s*out)\b/i
const WEBGPU_COMPATIBILITY_FAILURE =
  /\b(?:gpu adapter|gpu device|gpu process|shader|unsupported (?:data type|dtype|op|operator)|webgpu)\b/i

function failureName(error: unknown): string {
  return (
    typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      typeof error.name === 'string'
      ? error.name
      : ''
  )
}

export function failureMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export function shouldFallbackFromWebGpu(
  error: unknown,
): boolean {
  const message = failureMessage(error)
  if (NON_COMPATIBILITY_FAILURE.test(message)) return false
  if (failureName(error) === 'NotSupportedError') return true
  return WEBGPU_COMPATIBILITY_FAILURE.test(message)
}

export function classifyEmbeddingFailure(
  error: unknown,
  phase: 'load' | 'inference',
): {
  code: EmbeddingErrorCode
  retryable: boolean
  message: string
} {
  const message = failureMessage(error)
  if (/allowlist|blocked a model request/i.test(message)) {
    return {
      code: 'POLICY_BLOCKED',
      retryable: false,
      message,
    }
  }
  if (/checksum|integrity|manifest does not match/i.test(message)) {
    return {
      code: 'INTEGRITY_FAILED',
      retryable: false,
      message,
    }
  }
  if (
    failureName(error) === 'NetworkError' ||
    /\b(?:failed to fetch|network|offline|timed?\s*out)\b/i.test(
      message,
    )
  ) {
    return {
      code: 'NETWORK_FAILED',
      retryable: true,
      message,
    }
  }
  if (shouldFallbackFromWebGpu(error)) {
    return {
      code: 'COMPATIBILITY_FAILED',
      retryable: false,
      message,
    }
  }
  return {
    code:
      phase === 'load'
        ? 'LOAD_FAILED'
        : 'INFERENCE_FAILED',
    retryable: true,
    message,
  }
}
